import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useEditor } from "../useEditorState";

// Archive only (soft delete): bookings, CRM contacts and history stay; future bookings stay valid and cancellable.
export function DeleteServiceDialog({ c, svc, onClose, onDone }) {
  const ed = useEditor();
  const [u, setU] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { ed.api.get(`/t/${c.tid}/booking/services/${svc.id}/usage`).then((r) => setU(r.data)).catch(() => setU({ future: 0, total: 0 })); }, [svc.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await ed.api.delete(`/t/${c.tid}/booking/services/${svc.id}`); toast.success(`Servizio «${svc.name}» eliminato (archiviato, ripristinabile)`); onDone(svc.id); }
    catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="svc-delete-dialog">
        <AlertDialogHeader><AlertDialogTitle>Eliminare «{svc.name}»?</AlertDialogTitle>
          <AlertDialogDescription asChild><div className="space-y-2 text-sm">
            <p>Il servizio sparisce dal widget e non accetta nuove prenotazioni. Nessun dato viene cancellato: prenotazioni, contatti CRM e storico restano.</p>
            {!u ? <p className="text-slate-500">Controllo le prenotazioni…</p>
              : u.future ? <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-800" data-testid="svc-delete-future"><b>{u.future} prenotazion{u.future === 1 ? "e futura resta" : "i future restano"} valid{u.future === 1 ? "a" : "e"}</b> nel Calendario CRM: non vengono annullate in automatico (puoi annullarle a mano) e non si possono spostare finché il servizio è eliminato.</p>
              : <p className="text-slate-500" data-testid="svc-delete-nofuture">Nessuna prenotazione futura{u.total ? ` (${u.total} nello storico, conservate)` : ""}.</p>}
            <p className="text-slate-500">Potrai ripristinarlo da «Servizi eliminati».</p></div></AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel data-testid="svc-delete-cancel">Annulla</AlertDialogCancel>
          <AlertDialogAction onClick={go} disabled={busy || !u} className="bg-red-600 hover:bg-red-700" data-testid="svc-delete-confirm">{busy && <Loader2 size={13} className="mr-1.5 animate-spin" />}Elimina servizio</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>);
}

export function ArchivedServices({ c, tick, onRestored }) {
  const ed = useEditor();
  const [list, setList] = useState([]);
  useEffect(() => { ed.api.get(`/t/${c.tid}/booking/services`, { params: { archived: true } }).then((r) => setList(r.data)).catch(() => {}); }, [c.tid, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!list.length) return null;
  const restore = async (x) => {
    try { await ed.api.post(`/t/${c.tid}/booking/services/${x.id}/restore`); toast.success(`«${x.name}» ripristinato`); onRestored(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <details className="rounded-md border border-dashed border-slate-200 px-2 py-1.5" data-testid="bw-archived">
      <summary className="cursor-pointer text-[11px] text-slate-500">Servizi eliminati ({list.length})</summary>
      <div className="mt-1.5 space-y-1">{list.map((x) => (
        <div key={x.id} className="flex items-center gap-2 text-xs" data-testid={`bw-archived-${x.id}`}><span className="min-w-0 flex-1 truncate text-slate-600">{x.name}</span>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => restore(x)} data-testid={`bw-restore-${x.id}`}><RotateCcw size={11} className="mr-1" />Ripristina</Button></div>))}</div>
    </details>);
}
